import {
  useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type RefObject,
} from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Download, Eye, FileText, Lock, Mail, Pencil, Pin, PinOff, Plus, Printer, Share2, Trash2,
} from '../../../icons';
import {
  Avatar, Button, EmptyState, InlineNote, PageHeader, SearchInput, Table, Tabs, toast, type TableColumn,
} from '../../../components/shared';
import { layerConfirm } from '../../../components/LayerPopup/LayerPopup';
import {
  deleteBoardPost, getBoardPost, pinBoardPost,
  type BoardAttachmentDto, type BoardDto, type BoardPostDetail,
} from '../../../api/boards';
import { requestPresignedDownload } from '../../../api/files';
import { attachmentPreviewDoc, type PreviewDoc } from '../../../lib/preview-doc';
import { formatBytes } from '../../../lib/attachments';
import { getBoardsLang, subscribeBoardsLang, tBoards } from '../../../lib/boards-i18n';
import { ComposeModal, type ComposePrefill } from '../../MailPage/ComposeModal';
import { BoardDocPanel } from '../../BoardsPage/wide/BoardDocPanel';
import { BoardMark, BoardPrefixPill } from '../BoardMarks';
import { confirmStateOf } from '../confirm-state';
import {
  boardListPath, boardPostEditPath, boardPostPath, boardWritePath, parsePostTab, type BoardPostTab,
} from '../board-v1-url';
import { listBoardPostComments } from '../../../api/board-comments';
import { BoardPostComments } from './BoardPostComments';
import { attachmentSummary, countComments, formatRecordDateTime, neighbours, readNavState } from './post-model';
import { useBoards } from '../boards-context';
// 본문이 HTML 인 글만 이것으로 그린다 — TEXT 는 종전 그대로(아래 렌더 주석 참조).
import { BoardContentHtml } from '../../../components/editor/BoardContentHtml';
import './board-post.css';

/**
 * 글 보기 — 시안 v1 `axi-board-post` (U3 · 2026-09-15 · 주소 `/board/post/:postId?tab=`).
 *
 *   머리(경로 · 게시판 이름 · 모든 게시판 찾기 · 글쓰기)
 *   [글 카드 | 오른쪽: 글 정보 · 이전·다음 글]   ← 문서뷰어를 열면 오른쪽이 뷰어가 된다
 *
 * 글 카드: 읽은 위치 막대 · 「‹ 목록」 줄(메일 · 인쇄 · 고정 · 수정 · 삭제) · 글 머리 · 탭 · 칸 · 이전/다음 줄.
 *
 * ★ 데이터 경로는 현행 `BoardReader` 와 같다 — `getBoardPost` · `pinBoardPost` · `deleteBoardPost` · presign · `BoardDocPanel`.
 * ★★ 확인 요청(R1)은 2026-09-16 에 서버가 들어왔다 — 머리에 **「확인 필요 / 확인함」 배지**를 단다.
 *    판정은 `../confirm-state.ts` **한 곳**이라 목록 배지와 같은 값을 본다.
 *    아직 안 그리는 것: 확인 띠 · 「내용 확인했어요」 단추 · 확인 현황 · 확인 이력 탭 · 머리의 「읽음 n / N」.
 *    (분모 `targetCount` 는 상세에만 오고, 목록에는 일부러 안 싣는다 — 차이표 §0.25.)
 * ★★ 알림 이력 탭은 작성자 · 운영자에게만 서고 내용은 「아직 없음」(R4 는 들어왔으나 웹은 아직 안 이었다).
 * ★★ 열람 권한 없는 글(관제 Q-E · 규칙 5): 탭은 본문 하나 · 첨부 · 댓글 수 · 글 정보의 댓글/첨부/알림 칸을 숨기고
 *    메일 · 인쇄 · 고정 · 수정 · 삭제 단추도 없다.
 * ★ 이전·다음은 목록이 넘긴 «받아 둔 목록»에서만 — 알림 · 주소로 들어오면 숨긴다(계획 §2.4).
 * ★ 머리에 「목록으로」 단추는 두지 않는다 — 카드 안 「‹ 목록」 하나(관제 지시).
 * ★ 본문은 **글자 그대로** 그린다(`TEXT`) — 남이 쓴 마크업을 실행하지 않는다(현행과 같음).
 */

type Loaded =
  | { status: 'loading' }
  | { status: 'ready'; post: BoardPostDetail; board: BoardDto }
  | { status: 'missing' }
  | { status: 'error'; message: string };

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

/** 확인창 문구의 보존 기간 — 서버 기본값(BOARD_TRASH_RETENTION_DAYS 30)과 같다(현행 `BoardReader` 와 같은 판단). */
const TRASH_RETENTION_DAYS = 30;

/** 읽은 위치 — 글 카드가 화면을 얼마나 지나갔나(0~1). 스크롤이 창이 아니라 안쪽 상자에서 일어나도 잡는다. */
function useReadProgress(ref: RefObject<HTMLElement>): number {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const room = r.height - window.innerHeight;
      setProgress(room > 0 ? Math.min(1, Math.max(0, -r.top / room)) : 0);
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [ref]);
  return progress;
}

export function BoardPostScreen({ postId }: { postId: string }) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  const tab = parsePostTab(sp);
  const nav = readNavState(location.state);

  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [warn, setWarn] = useState<string | null>(null);
  const [viewer, setViewer] = useState<PreviewDoc | null>(null);
  const [viewerWide, setViewerWide] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const [headDraft, setHeadDraft] = useState('');

  /**
   * 좌측 메뉴에 「지금 이 글이 어느 게시판인지」를 알린다 (2026-09-17).
   * 주소에 게시판 id 가 없는 화면이라 이걸 안 하면 좌측이 아무것도 안 골라진 채로 선다.
   * 떠날 때 `null` 로 되돌린다 — 다른 화면에서 옛 게시판이 골라져 있지 않게.
   */
  const { setViewingBoardId } = useBoards();
  const loadedBoardId = loaded.status === 'ready' ? loaded.board.id : null;
  useEffect(() => {
    setViewingBoardId(loadedBoardId);
    return () => setViewingBoardId(null);
  }, [loadedBoardId, setViewingBoardId]);
  const articleRef = useRef<HTMLElement>(null);
  const progress = useReadProgress(articleRef);

  useEffect(() => {
    let cancelled = false;
    setLoaded({ status: 'loading' });
    setWarn(null);
    // 다른 글로 넘어가면 열려 있던 첨부 패널은 닫는다 — 남의 글 첨부가 남아 있으면 안 된다(현행과 같음).
    setViewer(null);
    setViewerWide(false);
    setCommentCount(null);
    getBoardPost(postId)
      .then((d) => { if (!cancelled) setLoaded({ status: 'ready', post: d.post, board: d.board }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as ApiError)?.response?.status;
        setLoaded(status === 404 || status === 403 ? { status: 'missing' } : { status: 'error', message: errMsg(e) });
      });
    return () => { cancelled = true; };
  }, [postId, attempt]);

  // D1 (2026-09-15 실측) — 글 상세 DTO 에 댓글 수가 없어 댓글 탭을 열기 전엔 「0」이 보였다.
  //   글을 열 때 댓글을 한 번 불러 수를 채우고, 불러오기 전에는 숫자 대신 자리표시를 둔다(0 금지).
  //   ★ 서버 요청 R18(상세 `commentCount`)이 들어오면 이 호출을 뺀다 — 아래 조건이 이미 그 경우는 건너뛴다.
  //   댓글 탭으로 바로 들어오면 `BoardPostComments` 가 불러 수를 올려 주므로 여기서는 부르지 않는다(두 번 부르지 않기).
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const readyPost = loaded.status === 'ready' ? loaded.post : null;
  const readyPostId = readyPost?.id ?? null;
  const needCount = !!readyPost && !readyPost.locked && readyPost.commentsAllowed && readyPost.commentCount == null;
  useEffect(() => {
    if (!needCount || !readyPostId || tabRef.current === 'comments') return;
    let cancelled = false;
    listBoardPostComments(readyPostId)
      .then((rows) => { if (!cancelled) setCommentCount(countComments(rows)); })
      .catch(() => { /* 모르면 자리표시 그대로 — 볼 수 없는 글(404)은 댓글 탭이 따로 알린다 */ });
    return () => { cancelled = true; };
  }, [needCount, readyPostId]);

  const setTab = (next: BoardPostTab) => {
    setSp(next === 'body' ? new URLSearchParams() : new URLSearchParams({ tab: next }), {
      replace: true,
      state: location.state,
    });
  };

  const crumb = tBoards('boards.v1.crumb');
  const board = loaded.status === 'ready' ? loaded.board : null;
  const post = loaded.status === 'ready' ? loaded.post : null;

  const submitHeadSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = headDraft.trim();
    if (!q || !board) return;
    navigate(boardListPath(board.id, { q, scope: 'all' }));
  };

  const backToList = () => {
    if (!board) { navigate('/board'); return; }
    navigate(`${boardListPath(board.id)}${nav?.boardId === board.id ? nav.search : ''}`);
  };

  async function download(fileId: string) {
    try {
      const res = await requestPresignedDownload(fileId, { disposition: 'attachment' });
      window.location.href = res.downloadUrl;
    } catch (e) {
      setWarn(tBoards('boards.v1.post.err_action', { msg: errMsg(e) }));
    }
  }

  async function togglePin() {
    if (!post || !board) return;
    try {
      const updated = await pinBoardPost(post.id, !post.isPinned);
      setLoaded({ status: 'ready', board, post: { ...post, isPinned: updated.isPinned, pinnedAt: updated.pinnedAt } });
    } catch (e) {
      setWarn(tBoards('boards.wide.pin_err', { msg: errMsg(e) }));
    }
  }

  async function remove() {
    if (!post || !board) return;
    const ok = await layerConfirm(tBoards('boards.v1.post.delete.body', { days: TRASH_RETENTION_DAYS }), {
      title: tBoards('boards.v1.post.delete.title'),
      confirmText: tBoards('boards.v1.post.delete.ok'),
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteBoardPost(post.id);
      navigate(boardListPath(board.id), { replace: true });
    } catch (e) {
      setWarn(tBoards('boards.v1.post.err_action', { msg: errMsg(e) }));
    }
  }

  async function copyLink() {
    if (!post) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${boardPostPath(post.id)}`);
      toast.success(tBoards('boards.v1.post.copied'));
    } catch (e) {
      setWarn(tBoards('boards.v1.post.err_action', { msg: errMsg(e) }));
    }
  }

  const header = (
    <PageHeader
      className="bv1-head"
      breadcrumb={[crumb, board?.name ?? crumb, tBoards('boards.v1.post.crumb')]}
      title={board?.name ?? crumb}
      actions={(
        <>
          <form role="search" className="bv1-head__search" onSubmit={submitHeadSearch}>
            <SearchInput
              value={headDraft}
              placeholder={tBoards('boards.v1.head.search_all')}
              aria-label={tBoards('boards.v1.head.search_all')}
              onChange={(e) => setHeadDraft(e.target.value)}
              onClear={() => setHeadDraft('')}
            />
          </form>
          <Button
            variant="primary"
            iconLeft={<Plus aria-hidden />}
            disabled={!board || board.canWrite === false}
            onClick={() => { if (board) navigate(boardWritePath(board.id)); }}
          >
            {tBoards('boards.btn.write')}
          </Button>
        </>
      )}
    />
  );

  const root = (children: React.ReactNode) => (
    <div className="bv1-page bv1-post" data-board-screen="post" data-id={postId} data-tab={tab}>
      {header}
      {children}
    </div>
  );

  if (loaded.status === 'loading') {
    return root(<section className="bv1-card bv1-main"><div className="bv1-loading" aria-busy="true">{tBoards('boards.v1.loading')}</div></section>);
  }

  if (loaded.status === 'missing' || loaded.status === 'error') {
    return root(
      <section className="bv1-card bv1-main">
        <div className="bv1-post__bar">
          <Button variant="secondary" size="compact" iconLeft={<ChevronLeft aria-hidden />} onClick={backToList}>
            {tBoards('boards.btn.back')}
          </Button>
        </div>
        {loaded.status === 'missing' ? (
          <EmptyState kind="no-permission" icon={Lock} title={tBoards('boards.v1.post.missing.title')} description={tBoards('boards.v1.post.missing.desc')} />
        ) : (
          <EmptyState
            kind="error"
            title={tBoards('boards.post.err_detail', { msg: loaded.message })}
            action={<Button variant="secondary" size="compact" onClick={() => setAttempt((n) => n + 1)}>{tBoards('boards.v1.retry')}</Button>}
          />
        )}
      </section>,
    );
  }

  // 여기부터 post · board 가 있다.
  const p = loaded.post;
  const b = loaded.board;
  const locked = p.locked;
  // 확인 요청 상태 — 목록 배지와 **같은 함수**로 판정한다(두 화면이 갈라지지 않게).
  const confirmState = confirmStateOf(p);
  const canSeeLog = !locked && (b.canManage === true || p.canEdit === true);
  const files = attachmentSummary(p.attachments);
  // D1 — 모르는 동안은 null(자리표시). 탭 수는 정본 Tabs 규칙대로 «모르면 주지 않는다», 글 정보 · 칩은 「—」.
  const shownComments: number | null = commentCount ?? p.commentCount ?? null;
  const nb = nav && nav.boardId === b.id ? neighbours(nav, p.id) : null;

  const tabs = locked
    ? [{ value: 'body', label: tBoards('boards.v1.post.tab.body') }]
    : [
      { value: 'body', label: tBoards('boards.v1.post.tab.body') },
      { value: 'files', label: tBoards('boards.v1.post.tab.files'), count: files.count },
      p.commentsAllowed
        ? { value: 'comments', label: tBoards('boards.v1.post.tab.comments'), ...(shownComments != null ? { count: shownComments } : {}) }
        : { value: 'comments', label: tBoards('boards.v1.post.tab.comments_off') },
      ...(canSeeLog ? [{ value: 'notify', label: tBoards('boards.v1.post.tab.notify') }] : []),
    ];
  const active = (tabs.some((t) => t.value === tab) ? tab : 'body') as BoardPostTab;

  // D2 — 문서뷰어를 넓히면 글 카드가 좁아진다: 고정 칸 폭을 풀어 단추가 줄바꿈되게 한다(CSS `is-wide` 짝).
  const narrowFiles = !!viewer && viewerWide;
  const fileColumns: TableColumn<BoardAttachmentDto>[] = [
    {
      key: 'name',
      header: tBoards('boards.v1.post.files.col_file'),
      render: (a) => <span className="bv1-file"><FileText aria-hidden className="bv1-file__icon" /><span className="bv1-file__name" title={a.name}>{a.name}</span></span>,
    },
    { key: 'size', header: tBoards('boards.v1.post.files.col_size'), numeric: true, width: narrowFiles ? '72px' : '96px', render: (a) => formatBytes(a.sizeBytes ?? a.size ?? 0) },
    {
      key: 'actions',
      header: tBoards('boards.v1.post.files.col_actions'),
      align: 'right',
      ...(narrowFiles ? {} : { width: '220px' }),
      render: (a) => {
        const doc = attachmentPreviewDoc({ fileId: a.fileId, name: a.name, mimeType: a.mimeType ?? undefined, sizeBytes: a.sizeBytes });
        return (
          <span className="bv1-file__acts">
            {doc && (
              <Button variant="secondary" size="compact" iconLeft={<Eye aria-hidden />} aria-label={tBoards('boards.aria.viewer', { name: a.name })} onClick={() => setViewer(doc)}>
                {tBoards('boards.btn.viewer')}
              </Button>
            )}
            <Button variant="secondary" size="compact" iconLeft={<Download aria-hidden />} onClick={() => void download(a.fileId)}>
              {tBoards('boards.btn.download')}
            </Button>
          </span>
        );
      },
    },
  ];

  const mailPrefill: ComposePrefill = {
    subject: p.title,
    body: p.content ?? '',
    // 2026-10-14 — HTML 글(AxiEditor)은 메일도 HTML 로 쓸 때 서식째 넘긴다. 평문으로 쓸 때는 위 `body` 다.
    ...(p.contentType === 'HTML' ? { bodyHtml: p.content ?? '' } : {}),
  };
  const visibility = p.isSecret
    ? tBoards('boards.v1.post.vis.secret')
    : tBoards(b.isPrivate ? 'boards.v1.post.vis.private_board' : 'boards.v1.post.vis.shared');
  const dash = tBoards('boards.v1.post.info.unknown');

  return root(
    <div className={`bv1-post__layout${viewer ? ' has-viewer' : ''}${viewer && viewerWide ? ' is-wide' : ''}`}>
      <article className="bv1-card bv1-post__card" ref={articleRef} aria-label={p.title}>
        <div className="bv1-readbar" aria-hidden><i style={{ transform: `scaleX(${progress})` }} /></div>

        <div className="bv1-post__bar">
          <Button variant="secondary" size="compact" iconLeft={<ChevronLeft aria-hidden />} onClick={backToList}>
            {tBoards('boards.btn.back')}
          </Button>
          <span className="bv1-post__where">
            <b>{b.name}</b>
            {nb && <span className="bv1-num"> · {tBoards('boards.wide.position', { index: nb.index, total: nb.total })}</span>}
          </span>
          {!locked && (
            <span className="bv1-post__acts">
              {b.showMailButton && (
                <Button variant="secondary" size="compact" iconLeft={<Mail aria-hidden />} onClick={() => setMailOpen(true)}>
                  {tBoards('boards.btn.mail')}
                </Button>
              )}
              <Button variant="secondary" size="compact" iconLeft={<Printer aria-hidden />} onClick={() => window.print()}>
                {tBoards('boards.wide.print')}
              </Button>
              {b.canManage && (
                <Button
                  variant="secondary"
                  size="compact"
                  aria-pressed={p.isPinned}
                  iconLeft={p.isPinned ? <PinOff aria-hidden /> : <Pin aria-hidden />}
                  onClick={() => void togglePin()}
                >
                  {tBoards(p.isPinned ? 'boards.wide.unpin' : 'boards.wide.pin')}
                </Button>
              )}
              <Button
                variant="secondary"
                size="compact"
                iconLeft={<Pencil aria-hidden />}
                disabled={p.canEdit === false}
                {...(p.canEdit === false ? { title: tBoards('boards.wide.edit_forbidden') } : {})}
                onClick={() => navigate(boardPostEditPath(p.id))}
              >
                {tBoards('boards.wide.edit')}
              </Button>
              {p.canEdit !== false && (
                <Button variant="quiet-danger" size="compact" iconLeft={<Trash2 aria-hidden />} onClick={() => void remove()}>
                  {tBoards('boards.btn.delete')}
                </Button>
              )}
            </span>
          )}
        </div>

        <header className="bv1-post__head">
          {p.author.anonymous || !p.author.id
            ? <span className="bv1-post__avatar-blank" aria-hidden />
            : <Avatar userId={Number(p.author.id)} name={p.author.name} src={p.author.avatarUrl ?? null} size="lg" />}
          <div className="bv1-post__headmain">
            <div className="bv1-post__titleline">
              <h2 className="bv1-post__title">{p.title}</h2>
              {p.isPinned && <BoardMark kind="pinned" />}
              {/*
                확인 요청 표식 (2026-09-16 · R1) — **목록과 같은 판정**을 쓴다(`confirm-state.ts`).
                두 화면이 각자 `requiresConfirm && !myConfirmedAt` 를 다시 쓰면 한쪽만 고쳐져 갈라진다.
                ★ 여기서도 숫자는 붙이지 않는다 — 확인 수는 확인 현황이 따로 그린다(R1 후속).
              */}
              {confirmState === 'required' && <BoardMark kind="confirm-required" />}
              {confirmState === 'confirmed' && <BoardMark kind="confirmed" />}
              {p.prefix && <BoardPrefixPill label={p.prefix.label} />}
              {p.isSecret && <BoardMark kind="secret" />}
              {!locked && !p.commentsAllowed && <BoardMark kind="comments-off" />}
            </div>
            <p className="bv1-post__author">{p.author.name}</p>
            <p className="bv1-post__meta bv1-num">
              {[
                formatRecordDateTime(p.createdAt),
                locked ? tBoards('boards.post.locked') : tBoards('boards.v1.post.meta_views', { n: p.viewCount }),
              ].join(' · ')}
            </p>
          </div>
        </header>

        {warn && <InlineNote tone="danger" className="bv1-post__warn">{warn}</InlineNote>}

        <div className="bv1-post__tabbar">
          <Tabs
            variant="pill"
            ariaLabel={tBoards('boards.v1.post.tabs_aria')}
            items={tabs}
            value={active}
            onChange={(v) => setTab(v as BoardPostTab)}
          />
          {!locked && (
            <Button variant="ghost" size="compact" iconOnly aria-label={tBoards('boards.v1.post.copy_link')} onClick={() => void copyLink()}>
              <Share2 aria-hidden />
            </Button>
          )}
        </div>

        <div className="bv1-post__pane" role="tabpanel" aria-label={tabs.find((t) => t.value === active)?.label}>
          {active === 'body' && (locked ? (
            <EmptyState
              kind="no-permission"
              icon={Lock}
              title={tBoards('boards.v1.post.deny.title')}
              description={tBoards('boards.v1.post.deny.desc')}
            />
          ) : (
            <>
              {/*
                본문 — `contentType` 으로 «갈라» 그린다 (2026-09-23).

                ⛔ 한쪽으로 몰면 한쪽이 깨진다. 옛 글(TEXT)과 새 글(HTML)이 **함께** 있고,
                   감사에서 운영에 살아 있는 글 3건이 모두 TEXT 였다 — **0건이 아니다.**
                ★ TEXT 갈래는 종전 그대로다(`.bv1-art` 의 `white-space: pre-wrap` 으로 줄바꿈 유지).
                ⛔ HTML 쪽에는 그 `pre-wrap` 을 쓰지 «않는다» — 안 그러면 태그 사이 개행이
                   빈 줄이 되어 문서가 성겨진다. 그래서 `.bv1-art` 를 붙이지 않고
                   `.axi-board-content`(pre-wrap 없음)로 그린다.
              */}
              {p.contentType === 'HTML'
                ? <BoardContentHtml html={p.content ?? ''} className="bv1-art--html" />
                : <div className="bv1-art">{p.content ?? ''}</div>}
              <div className="bv1-react">
                {p.commentsAllowed && (
                  <button type="button" className="bv1-react__btn" onClick={() => setTab('comments')}>
                    {shownComments != null
                      ? tBoards('boards.v1.post.react.comments', { n: shownComments })
                      : tBoards('boards.v1.post.react.comments_unknown')}
                  </button>
                )}
                <button type="button" className="bv1-react__btn" onClick={() => void copyLink()}>
                  {tBoards('boards.v1.post.copy_link')}
                </button>
              </div>
            </>
          ))}

          {active === 'files' && (
            <>
              <Table
                caption={tBoards('boards.v1.post.files.caption', { title: p.title })}
                columns={fileColumns}
                rows={p.attachments}
                rowKey={(a) => a.fileId}
                empty={tBoards('boards.v1.post.files.empty')}
                wrapClassName="bv1-tablewrap"
              />
              {p.attachments.length > 0 && <p className="bv1-fnote">{tBoards('boards.v1.post.files.note')}</p>}
            </>
          )}

          {active === 'comments' && <BoardPostComments post={p} onCountChange={setCommentCount} />}

          {active === 'notify' && (
            <EmptyState title={tBoards('boards.v1.kpi.not_ready')} description={tBoards('boards.v1.post.notify.not_ready')} />
          )}
        </div>

        {nb && (
          <nav className="bv1-post__savebar" aria-label={tBoards('boards.wide.nav_aria')}>
            <Button
              variant="secondary"
              size="compact"
              iconLeft={<ChevronLeft aria-hidden />}
              disabled={!nb.prev}
              onClick={() => { if (nb.prev) navigate(boardPostPath(nb.prev.id), { state: location.state }); }}
            >
              {tBoards('boards.wide.prev')}
            </Button>
            <span className="bv1-post__savenote">{tBoards('boards.v1.post.nav.note')}</span>
            <Button
              variant="secondary"
              size="compact"
              iconRight={<ChevronRight aria-hidden />}
              disabled={!nb.next}
              onClick={() => { if (nb.next) navigate(boardPostPath(nb.next.id), { state: location.state }); }}
            >
              {tBoards('boards.wide.next')}
            </Button>
          </nav>
        )}
      </article>

      <aside className="bv1-post__side">
        {viewer ? (
          <BoardDocPanel
            doc={viewer}
            expanded={viewerWide}
            onToggleExpand={() => setViewerWide((v) => !v)}
            onDownload={() => { if (viewer.source.kind === 'attachment') void download(viewer.source.fileId); }}
            onClose={() => { setViewer(null); setViewerWide(false); }}
          />
        ) : (
          <>
            <section className="bv1-card bv1-side-card" aria-label={tBoards('boards.v1.post.info.title')}>
              <h3 className="bv1-side-card__title">{tBoards('boards.v1.post.info.title')}</h3>
              <dl className="bv1-kvs">
                <dt>{tBoards('boards.v1.post.info.board')}</dt><dd>{b.name}</dd>
                <dt>{tBoards('boards.v1.post.info.prefix')}</dt><dd>{p.prefix?.label ?? tBoards('boards.editor.prefix_none')}</dd>
                <dt>{tBoards('boards.v1.post.info.visibility')}</dt><dd>{visibility}</dd>
                <dt>{tBoards('boards.v1.post.info.comments')}</dt>
                <dd>
                  {locked
                    ? dash
                    : !p.commentsAllowed
                      ? tBoards('boards.v1.post.info.comments_off')
                      : shownComments != null
                        ? tBoards('boards.v1.post.info.comments_on', { n: shownComments })
                        : tBoards('boards.v1.post.info.comments_on_unknown')}
                </dd>
                <dt>{tBoards('boards.v1.post.info.files')}</dt>
                <dd className="bv1-num">
                  {locked ? dash : files.count > 0 ? tBoards('boards.v1.post.info.files_some', { n: files.count, size: formatBytes(files.bytes) }) : tBoards('boards.v1.post.info.files_none')}
                </dd>
                <dt>{tBoards('boards.v1.post.info.notify')}</dt>
                <dd>{locked ? dash : tBoards('boards.v1.kpi.not_ready')}</dd>
              </dl>
            </section>

            {nb && (
              <section className="bv1-card bv1-side-card" aria-label={tBoards('boards.v1.post.nav.title')}>
                <h3 className="bv1-side-card__title">{tBoards('boards.v1.post.nav.title')}</h3>
                <div className="bv1-navpost">
                  {([['prev', nb.prev], ['next', nb.next]] as const).map(([k, target]) => (
                    target ? (
                      <Link key={k} to={boardPostPath(target.id)} state={location.state} className="bv1-navpost__item">
                        <small>{tBoards(k === 'prev' ? 'boards.wide.prev' : 'boards.wide.next')}</small>
                        <b>{target.title || dash}</b>
                      </Link>
                    ) : (
                      <span key={k} className="bv1-navpost__item is-none">
                        <small>{tBoards(k === 'prev' ? 'boards.wide.prev' : 'boards.wide.next')}</small>
                        <b>{dash}</b>
                      </span>
                    )
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </aside>

      <ComposeModal open={mailOpen} prefill={mailPrefill} onClose={() => setMailOpen(false)} />
    </div>,
  );
}
